import { GCashButton } from '@src/components/Button';
import { AnimatePresence, motion } from 'framer-motion';
import { Fragment, useCallback, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { AlertAction, AlertProps } from './AlertProvider';
import { useAlert } from './AlertProvider';

interface AlertComponentProps extends AlertProps {
  isOpen: boolean;
  onClose: () => void;
  content?: ReactNode;
}

export function Alert({ isOpen, onClose, title, message, actions = [], content }: AlertComponentProps) {
  const handleActionPress = (action: AlertAction) => {
    action.onPress?.();
    onClose();
  };

  const handleDismiss = () => {
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <Fragment>
          {/* Backdrop */}
          <motion.div
            key="alert-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.4 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="bg-black-100 fixed inset-0 z-40"
            onClick={handleDismiss}
          />

          {/* Alert Container */}
          <motion.div
            key="alert-container"
            data-testid="alert-container"
            initial={{ opacity: 0, scale: 0.8, y: 20, transform: 'translate(-50%, -50%)' }}
            animate={{ opacity: 1, scale: 1, y: 0, transform: 'translate(-50%, -50%)' }}
            exit={{ opacity: 0, scale: 0.8, y: 20, transform: 'translate(-50%, -50%)' }}
            transition={{
              duration: 0.3,
              type: 'spring',
              damping: 25,
              stiffness: 300,
            }}
            className="bg-white-100 fixed top-1/2 left-1/2 z-50 w-80 max-w-[90vw] rounded-2xl"
            style={{
              transform: 'translate(-50%, -50%)',
            }}
          >
            <div className="flex max-h-[80vh] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
              {/* Content */}
              <div className="space-y-4 p-5">
                {title && (
                  <h2
                    className="text-center text-lg leading-tight font-semibold text-gray-900"
                    data-testid="alert-title"
                  >
                    {title}
                  </h2>
                )}

                {message && (
                  <p className="text-center text-base leading-relaxed text-gray-600" data-testid="alert-message">
                    {message}
                  </p>
                )}

                {content && <div data-testid="alert-content">{content}</div>}
              </div>

              {/* Actions */}
              {actions.length > 0 && (
                <div className="p-2" data-testid="alert-actions">
                  {actions.length === 1 ? (
                    // Single button - full width
                    <GCashButton
                      data-testid="alert-action-button"
                      onClick={() => handleActionPress(actions[0])}
                      className="w-full"
                    >
                      {actions[0].title}
                    </GCashButton>
                  ) : (
                    // Multiple buttons - horizontal layout
                    <div className="flex">
                      {actions.map((action, index) => (
                        <Fragment key={action.title}>
                          <GCashButton
                            data-testid="alert-action-button"
                            onClick={() => handleActionPress(action)}
                            className={`h-12 flex-1 border-0 text-base font-medium ${
                              action.style === 'destructive'
                                ? 'text-red-600 hover:bg-red-50'
                                : action.style === 'cancel'
                                  ? 'text-gray-500 hover:bg-gray-50'
                                  : 'text-blue-600 hover:bg-gray-50'
                            }`}
                          >
                            {action.title}
                          </GCashButton>
                          {index < actions.length - 1 && <div className="h-full w-px bg-gray-200" />}
                        </Fragment>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </Fragment>
      )}
    </AnimatePresence>
  );
}

// Global Alert Component that uses the provider
export function GlobalAlert() {
  const alert = useAlert();
  const handleClose = useCallback(() => {
    alert.close();
  }, [alert]);

  if (!alert.isOpen) {
    return null;
  }

  return createPortal(
    <Alert
      isOpen={alert.isOpen}
      onClose={handleClose}
      title={alert.title}
      message={alert.message}
      actions={alert.actions}
      content={alert.content}
    />,
    document.body
  );
}
